<template>
  <ProductCardWrapper
    :title="productName"
    :full-product-price="fullProductPrice"
    :image="productMainImage"
    :discounted-price="discountedPrice"
    :is-discounted="isDiscounted"
    :last30-days-price="last30DaysPrice"
    :link="productLink"
    :tags="localisedAttributes[PRODUCT_ATTRIBUTES.TAGS]"
    :brand="localisedAttributes[PRODUCT_ATTRIBUTES.BRAND]"
    :sku="sku"
    :reviews-count="reviewsCount"
    :rating="rating"
    :price-guarantee-url="priceGuaranteeUrl"
    :stock-count="stockCount"
    :is-freight="localisedAttributes[PRODUCT_ATTRIBUTES.IS_PACKAGE_TYPE_FREIGHT]"
    :parcel-delivery-date="parcelDeliveryDate"
    :freight-delivery-date="freightDeliveryDate"
    :dimensions="dimensions"
    :key-facts="localisedAttributes[PRODUCT_ATTRIBUTES.HIGHLIGHTS]"
    :release-date="releaseDateString"
    :energy-rating="energyRating"
    :currency="currencyCode"
    :categories="categories"
    :product-box-type-tag="productBoxClickLocation"
    :size="size"
    @product-clicked="productSliderItemClicked"
  />
</template>

<script lang="ts">

import { productGetters } from '~/utils/ct';

import { getProductQuantity } from '~~/helpers/product/getProductStockInfo/getProductStockInfo';
import { PRODUCT_ATTRIBUTES } from '~~/constants/products';
import type { ProductVariantExtended } from '~~/types/product/ProductVariantExtended';
import type { ProductProcessedAttributes } from '~~/types/product/attribute/ProductProcessedAttributes';
import {
  getProductAttributesExtended,
} from '~~/helpers/product/getProductAttributesExtended';
import isString from '~~/helpers/dataStructure/isString';
import { getEnergyRating } from '~~/helpers/product/energyRating';
import type { ProductCardSize } from '~~/constants/productCardSize';
import { PRODUCT_CARD_SIZE } from '~~/constants/productCardSize';
import { addNativeRoutingToProductUrl } from '~~/helpers/routes/nativeRouting/transformProductsForNativeRouting';
import type { EventSourceLocation } from '~~/types/integrations/tagManager/TagManagerIntegration';
import { getCategoriesStringFromProduct } from '~~/helpers/category/getCategoryStringFromProduct';

export default defineComponent({
  name: 'ProductSliderItem',

  props: {
    product: {
      type: Object as PropType<ProductVariantExtended>,
      required: true,
    },
    priceGuaranteeUrl: {
      type: String,
      required: true,
    },
    parcelDeliveryDate: {
      type: String,
      default: '',
    },
    freightDeliveryDate: {
      type: String,
      default: '',
    },
    size: {
      type: String as PropType<ProductCardSize>,
      default: PRODUCT_CARD_SIZE.RESPONSIVE,
      validator: (value: ProductCardSize) => {

        return Object.values(PRODUCT_CARD_SIZE).includes(value);
      
      },
    },
    productBoxTypeTag: {
      type: String as PropType<EventSourceLocation>,
      default: '',
    },
  },
  emits: ['product-slider-item-clicked'],
  setup(props, { emit }) {

    const { product } = toRefs(props);

    const { languageAndCountry, countryCode } = useLocalization();
    const { getPriceInformation } = useProductPrice();

    const productName: ComputedRef<string> = computed(() => productGetters.getName(props.product));
    const productMainImage: ComputedRef<string> = computed(() => productGetters.getCoverImage(props.product));
    const sku: ComputedRef<string> = computed(() => productGetters.getSku(props.product));
    const reviewsCount: ComputedRef<number> = computed(() => productGetters.getTotalReviews(props.product));
    const rating: ComputedRef<number> = computed(() => productGetters.getAverageRating(props.product));
    const stockCount: ComputedRef<number> = computed(() => getProductQuantity(props.product));
    const localisedAttributes: ComputedRef<ProductProcessedAttributes> = computed(() => getProductAttributesExtended(
      props.product,
      {
        attributeNames: [
          PRODUCT_ATTRIBUTES.TAGS,
          PRODUCT_ATTRIBUTES.BRAND,
          PRODUCT_ATTRIBUTES.IS_PACKAGE_TYPE_FREIGHT,
          PRODUCT_ATTRIBUTES.HIGHLIGHTS,
          PRODUCT_ATTRIBUTES.ENERGY_RATING,
          PRODUCT_ATTRIBUTES.ENERGY_RATING_FICHE,
        ],
        languageAndCountry: languageAndCountry.value,
      }),
    );

    const dimensions = computed(() => {

      const productDimension = getProductAttributesExtended(
        props.product,
        {
          attributeNames: [
            PRODUCT_ATTRIBUTES.NETTO_HEIGHT,
            PRODUCT_ATTRIBUTES.NETTO_WIDTH,
            PRODUCT_ATTRIBUTES.NETTO_LENGTH,
          ],
          languageAndCountry: languageAndCountry.value,
        });
      const height = productDimension[PRODUCT_ATTRIBUTES.NETTO_HEIGHT];
      const width = productDimension[PRODUCT_ATTRIBUTES.NETTO_WIDTH];
      const length = productDimension[PRODUCT_ATTRIBUTES.NETTO_LENGTH];
      const hasDimensions = !!height && !!width && !!length;

      return {
        height,
        width,
        length,
        hasDimensions,
      };
    
    });

    const energyRating = computed(() => {

      return getEnergyRating({ product: props.product, languageAndCountry: languageAndCountry.value });
    
    });
    const { fullProductPrice, discountedPrice, isDiscounted, last30DaysPrice } = getPriceInformation(product);

    const { productBoxClickLocation } = useProductBoxClickLocation(props.productBoxTypeTag);

    const productSliderItemClicked = () => {

      emit('product-slider-item-clicked', sku.value);
    
    };

    const releaseDate = computed(() =>
      getProductAttributesExtended(
        props.product,
        {
          attributeNames: [PRODUCT_ATTRIBUTES.RELEASE_DATE],
        },
      )[PRODUCT_ATTRIBUTES.RELEASE_DATE],
    );
    const releaseDateString = computed(() => isString(releaseDate.value) ? releaseDate.value : '');

    const categories = computed(() => {

      return getCategoriesStringFromProduct(props.product);
    
    });

    const productLink = computed(() => {

      return addNativeRoutingToProductUrl(props.product?._slug, countryCode.value);
    
    });

    const currencyCode = computed(() => {

      return props.product?.price?.value?.currencyCode || '';
    
    });

    return {
      PRODUCT_ATTRIBUTES,
      productName,
      sku,
      reviewsCount,
      rating,
      stockCount,
      localisedAttributes,
      fullProductPrice,
      discountedPrice,
      isDiscounted,
      last30DaysPrice,
      dimensions,
      productMainImage,
      releaseDateString,
      categories,
      productBoxClickLocation,
      productLink,
      productSliderItemClicked,
      energyRating,
      getEnergyRating,
      currencyCode,
    };
  
  },
});

</script>
